<script setup lang="ts">
import type { HtmlParserPlugins } from '~/plugins/dom-parser'
import type { ContentSource } from '~/utils/content-normalization/content-normalizer'
import { sourcePlugins } from '~/utils/content-normalization/content-normalizer'

/**
 * BodyText - Generic component for rendering formatted content
 *
 * Encapsulates all shared content styling for consistent formatting
 * across law, literature, and journal components.
 *
 * Components can add component-specific styles targeting .body-text
 * and its children from within their own scoped styles.
 */

const props = defineProps<{
  html: string | null
  source?: ContentSource
  parserConfig?: {
    plugins: HtmlParserPlugins
    options?: Record<string, unknown>
  }
}>()

const { $parseStringToDocument } = useNuxtApp()
const bodyTextRef = ref<HTMLElement | null>(null)

const JATS_GRAPHIC_IMAGE_CLASS = 'jats-graphic-image'
const JATS_GRAPHIC_FALLBACK_CLASS = 'jats-graphic-fallback'
const JATS_GRAPHIC_FALLBACK_LABEL = 'Illustrasjon utilgjengelig'

const normalizedHtml = computed(() => {
  if (!props.html) {
    return ''
  }

  const basePlugins = (props.source ? sourcePlugins[props.source] : []) as HtmlParserPlugins

  const plugins = props.parserConfig
    ? [...basePlugins, ...props.parserConfig.plugins]
    : basePlugins

  if (!plugins || plugins.length === 0) {
    return props.html
  }

  return $parseStringToDocument(props.html, plugins, props.parserConfig?.options).body.innerHTML
})

function createJatsGraphicFallback(label?: string) {
  const fallback = document.createElement('div')
  fallback.className = JATS_GRAPHIC_FALLBACK_CLASS
  fallback.setAttribute('role', 'img')
  fallback.setAttribute('aria-label', label || JATS_GRAPHIC_FALLBACK_LABEL)
  return fallback
}

function hydrateBrokenJatsGraphics() {
  const container = bodyTextRef.value
  if (!container) {
    return
  }

  container.querySelectorAll<HTMLImageElement>(`img.${JATS_GRAPHIC_IMAGE_CLASS}`).forEach((img) => {
    if (img.dataset.jatsErrorBound === 'true') {
      return
    }

    const replaceWithFallback = () => {
      img.replaceWith(createJatsGraphicFallback(img.alt))
    }

    img.addEventListener('error', replaceWithFallback, { once: true })
    img.dataset.jatsErrorBound = 'true'

    if (img.complete && img.naturalWidth === 0) {
      replaceWithFallback()
    }
  })
}

onMounted(() => {
  nextTick(() => {
    hydrateBrokenJatsGraphics()
  })
})

watch(normalizedHtml, async () => {
  await nextTick()
  hydrateBrokenJatsGraphics()
})
</script>

<template>
  <div
    v-if="html"
    ref="bodyTextRef"
    class="body-text"
    v-html="normalizedHtml"
  />
</template>

<style scoped lang="postcss">
.body-text :deep() {
  /*
   * ==========================================
   * Shared Typography & Spacing (Gold Standard)
   * ==========================================
   */

  p {
    @apply mb-m dark:text-white leading-6;
  }

  p.ded2 {
    @apply mb-0.5 dark:text-white;
  }

  p.ded2:last-of-type {
    @apply mb-1.5 dark:text-white;
  }

  p.ded3 {
    @apply mt-s dark:text-white;
  }

  p.index-head {
    @apply font-semibold dark:text-white;
  }

  p:empty {
    @apply m-0 p-0 !mb-0;
  }

  /* Headings */
  h3,
  .heading {
    @apply font-body text-xl sm:text-base font-bold mb-m dark:text-white;
  }

  /* JATS section labels are structural markers and should render on their own line. */
  label {
    @apply block font-body text-sm sm:text-base dark:text-white;
  }

  h4,
  h5 {
    @apply font-body text-xl sm:text-base font-bold italic mb-m dark:text-white;
  }

  /* Images & Tables */
  img {
    @apply my-l block w-full max-w-full h-auto;
  }

  .jats-graphic-fallback {
    @apply my-l w-full max-w-full rounded-lg border border-border-secondary dark:border-carbon-200 bg-surface-secondary relative overflow-hidden;
    min-height: 12em;
    aspect-ratio: 16 / 9;
    background-image: linear-gradient(160deg, rgba(15, 38, 74, 0.08) 0%, rgba(15, 38, 74, 0.02) 100%);

    &::before,
    &::after {
      content: '';
      position: absolute;
      border-radius: 9999px;
      background: rgba(15, 38, 74, 0.15);
    }

    &::before {
      width: 38px;
      height: 38px;
      top: 20px;
      right: 20px;
    }

    &::after {
      width: 180px;
      height: 180px;
      bottom: -92px;
      left: -28px;
      background: rgba(15, 38, 74, 0.12);
      box-shadow: 110px -34px 0 0 rgba(15, 38, 74, 0.08);
    }
  }

  table {
    @apply mb-m w-full flex flex-col;

    thead > tr {
      @apply flex items-stretch gap-x-3xs;

      th {
        @apply text-left font-semibold px-2xs py-xs bg-surface-secondary flex-1;
        word-wrap: break-word;
        hyphens: auto;
      }
    }

    tbody {
      @apply flex flex-col gap-2xs flex-1;
    }

    tbody > tr {
      @apply flex items-stretch text-wrap gap-x-3xs;

      td {
        @apply block text-balance items-start px-2xs py-xs bg-surface-primary font-body text-sm sm:text-base flex-1;
        word-break: break-word;
      }
    }
  }

  figcaption {
    @apply font-body text-xs leading-5 sm:leading-6 mb-l italic;
  }

  .jats-figure-label {
    @apply block;
  }

  /*
   * ==========================================
   * Special Text Elements
   * ==========================================
   */

  /* Blockquotes (Standardized) */
  blockquote {
    @apply ml-s font-body text-sm sm:text-base text-primary;

    p {
      @apply ml-s font-body text-sm sm:text-base dark:text-white;
    }

    /* Keep paragraph and unindented list content aligned within quotes */
    ol.unindented,
    ul,
    ul.list-dash {
      @apply ml-s;
    }
  }

  /* Petit (Small text) */
  .petit {
    @apply font-body text-xs ml-s leading-5 sm:text-sm sm:leading-6 block;

    p,
    a,
    li {
      @apply text-[0.9rem] leading-[1.5rem];
    }
  }

  /*
   * ==========================================
   * Lists (Standardized Classes)
   * ==========================================
   * Note: Content normalizer should convert source-specific styles
   * to these classes before rendering.
   */

  ul,
  ol {
    @apply mb-m pl-m;
    list-style: none !important;
    list-style-type: none !important;
  }

  li {
    @apply mb-0 font-body text-sm sm:text-base pl-m relative;
    list-style: none !important;
    list-style-type: none !important;

    p {
      @apply mb-0;
    }

    &:last-child {
      @apply mb-0;
    }

    /* Nested lists indentation */
    ul,
    ol {
      @apply mt-xs mb-0;
      padding-left: 16px;
    }

    > ol.unindented.list-lower-alpha-paren,
    > ol.unindented.list-lower-roman-paren {
      margin-top: 0;
    }
  }

  /* Keep nested roman sublists visually nested under alpha-parenthesis lists */
  ol.list-lower-alpha-paren > li > ol.unindented.list-lower-roman-paren {
    margin-left: 16px;
  }

  /* Ordered List Counters */
  ol[data-listitem-continue='true'] > li::before {
    @apply sr-only;
  }

  ol {
    counter-reset: list-counter;

    > li {
      counter-increment: list-counter;

      /* Default Decimal (1.) */
      &::before {
        content: counter(list-counter) '.';
        position: absolute;
        left: 0;
        @apply font-body;
      }
    }

    /* Parentheses Decimal (1) */
    &.list-decimal-paren > li::before {
      content: counter(list-counter) ')';
    }

    /* Lower Alpha (a.) */
    &.list-lower-alpha > li::before {
      content: counter(list-counter, lower-alpha) '.';
    }

    /* Lower Alpha Paren (a)) */
    &.list-lower-alpha-paren > li::before {
      content: counter(list-counter, lower-alpha) ')';
    }

    /* Lower Roman (i.) */
    &.list-lower-roman > li::before {
      content: counter(list-counter, lower-roman) '.';
    }

    /* Lower Roman Paren (i)) */
    &.list-lower-roman-paren > li::before {
      content: counter(list-counter, lower-roman) ')';
    }

    /* Unstyled ordered list (no generated marker) */
    &.list-unstyled {
      padding-left: 0;

      > li {
        padding-left: 0;

        &::before {
          content: none;
        }
      }
    }

    /* Unindented ordered list (flush-left with inline marker) */
    &.unindented {
      padding-left: 0;

      > li {
        padding-left: 0;

        &::before {
          position: static;
          margin-right: 0.25rem;
        }
      }
    }
  }

  /* Default Bullet (•) */
  ul li::before {
    content: '•';
    position: absolute;
    left: 0;
    @apply font-body;
  }

  /* Dash List (–) */
  ul.list-dash li::before {
    content: '–';
  }

  /* Bibliography list: no visual list markers */
  ul.biblio-list li::before,
  ol.biblio-list > li::before {
    content: none;
  }

  /*
   * ==========================================
   * Footnotes
   * ==========================================
   */

  @keyframes footnoteFadeSlide {
    0% {
      opacity: 0;
      transform: translateY(4px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .footnote__content:not([hidden]) {
    @apply flex flex-col gap-2xs max-w-full box-border my-s rounded-lg bg-blue-50 dark:bg-carbon-500 text-sm break-all leading-6 p-s;
    animation: footnoteFadeSlide 0.22s ease-out;

    a {
      @apply text-sm;
    }
  }

  .footnote__content[hidden] {
    @apply !hidden;
  }

  .footnote__label {
    @apply font-semibold text-primary mr-3xs;
  }

  .footnote__text {
    @apply break-words font-normal;
    word-break: break-word;
  }

  .footnote__btn {
    @apply cursor-pointer underline text-primary;
  }

  @media (pointer: coarse) {
    sup .footnote__btn {
      @apply relative;

      &::after {
        content: '';
        @apply absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-11;
      }
    }
  }

  .footnote__close-btn {
    @apply rounded-lg border border-blurple-500 hover:bg-blurple-100 hover:border-blurple-100 dark:border-carbon-300 dark:hover:bg-carbon-400 p-2xs h-fit flex items-center justify-center transition-colors;

    svg {
      @apply h-4 w-4;
    }
  }
}
</style>
